import React, { useState } from 'react';
import {
    Card,
    Button,
    DatePicker,
    InputNumber,
    Table,
    Space,
    Typography,
    App,
    Popconfirm,
    Tag,
    Flex
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    CalendarOutlined,
    EuroOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const SeasonalPricing = ({
    seasonalPrices = [],
    onSeasonalPricesChange,
    basePrice,
    currency = '€'
}) => {
    const { message } = App.useApp();
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [price, setPrice] = useState('');

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        if (dates) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    };

    // Add seasonal price
    const handleAdd = () => {
        // Validation
        if (!startDate || !endDate) {
            message.error(t('admin.seasonalPricing.errors.selectDates'));
            return;
        }

        if (!price || price <= 0) {
            message.error(t('admin.seasonalPricing.errors.validPrice'));
            return;
        }

        // Check for overlaps
        const newStart = startDate.format('YYYY-MM-DD');
        const newEnd = endDate.format('YYYY-MM-DD');

        const hasOverlap = seasonalPrices.some(season => {
            const existingStart = season.start_date;
            const existingEnd = season.end_date;

            return (
                (newStart >= existingStart && newStart <= existingEnd) ||
                (newEnd >= existingStart && newEnd <= existingEnd) ||
                (newStart <= existingStart && newEnd >= existingEnd)
            );
        });

        if (hasOverlap) {
            message.error(t('admin.seasonalPricing.errors.overlap'));
            return;
        }

        // Add new seasonal price
        const newSeasonalPrice = {
            id: Date.now(), // Temporary ID for frontend
            start_date: newStart,
            end_date: newEnd,
            price: parseFloat(price),
        };

        const updatedPrices = [...seasonalPrices, newSeasonalPrice].sort((a, b) =>
            new Date(a.start_date) - new Date(b.start_date)
        );

        onSeasonalPricesChange(updatedPrices);

        // Reset form
        setStartDate(null);
        setEndDate(null);
        setPrice('');
        message.success(t('admin.seasonalPricing.success.added'));
    };

    // Remove seasonal price
    const handleRemove = (id) => {
        const updatedPrices = seasonalPrices.filter(season => season.id !== id);
        onSeasonalPricesChange(updatedPrices);
        message.success(t('admin.seasonalPricing.success.removed'));
    };

    // Table columns
    const columns = [
        {
            title: t('admin.seasonalPricing.table.season'),
            key: 'season',
            render: (_, record) => (
                <Flex vertical gap="small">
                    <Text strong>{getSeasonName(record.start_date, record.end_date)}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(record.start_date).format('MMM D, YYYY')} - {dayjs(record.end_date).format('MMM D, YYYY')}
                    </Text>
                </Flex>
            ),
        },
        {
            title: t('admin.seasonalPricing.table.duration'),
            key: 'duration',
            render: (_, record) => {
                const days = dayjs(record.end_date).diff(dayjs(record.start_date), 'day') + 1;
                return <Tag color="blue">{days} {t('admin.seasonalPricing.table.days')}</Tag>;
            },
        },
        {
            title: t('admin.seasonalPricing.table.price'),
            key: 'price',
            render: (_, record) => (
                <Space>
                    <Text strong style={{ fontSize: '16px', color: '#4d6765' }}>
                        {currency}{record.price}
                    </Text>
                    {basePrice && record.price !== basePrice && (
                        <Tag color={record.price > basePrice ? 'red' : 'green'}>
                            {record.price > basePrice ? '+' : ''}
                            {((record.price - basePrice) / basePrice * 100).toFixed(0)}%
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: t('admin.seasonalPricing.table.action'),
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={t('admin.seasonalPricing.confirm.title')}
                    description={t('admin.seasonalPricing.confirm.description')}
                    onConfirm={() => handleRemove(record.id)}
                    okText={t('admin.common.yes')}
                    cancelText={t('admin.common.no')}
                >
                    <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                    >
                        {t('admin.common.remove')}
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    // Helper to get season name
    const getSeasonName = (startDate, endDate) => {
        const start = dayjs(startDate);
        const startMonth = start.month();

        if (startMonth >= 11 || startMonth <= 1) return t('admin.seasonalPricing.seasons.winter');
        if (startMonth >= 2 && startMonth <= 4) return t('admin.seasonalPricing.seasons.spring');
        if (startMonth >= 5 && startMonth <= 7) return t('admin.seasonalPricing.seasons.summer');
        if (startMonth >= 8 && startMonth <= 10) return t('admin.seasonalPricing.seasons.fall');

        return t('admin.seasonalPricing.seasons.custom');
    };

    // Disable dates that are already covered
    const disabledDate = (current) => {
        if (!current) return false;

        // Disable past dates
        if (current < dayjs().startOf('day')) return true;

        const currentDate = current.format('YYYY-MM-DD');

        // Check if date falls within existing range
        return seasonalPrices.some(season => {
            return currentDate >= season.start_date && currentDate <= season.end_date;
        });
    };

    return (
        <Card
            title={
                <span style={{ fontSize: '16px', fontWeight: 600 }}>
                    <CalendarOutlined style={{ marginRight: '8px', color: '#4d6765' }} />
                    {t('admin.seasonalPricing.title')}
                </span>
            }
            style={{
                marginTop: '24px',
                border: '2px solid #e8e8e8',
                borderRadius: '8px',
            }}
        >
            {/* Info Banner */}
            {/* <div style={{
                padding: '12px 16px',
                backgroundColor: '#e6f4ff',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #91caff'
            }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                    💡 <strong>{t('admin.seasonalPricing.tip.label')}:</strong> {t('admin.seasonalPricing.tip.text')}
                    {' '}<strong>{currency}{basePrice || 0}/{t('admin.seasonalPricing.perNight')}</strong>
                </Text>
            </div> */}

            {/* Add Seasonal Price Form */}
            <div style={{
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #e8e8e8'
            }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: '16px' }}>
                    {t('admin.seasonalPricing.addTitle')}
                </Title>

                <Flex vertical gap="middle" style={{ width: '100%' }}>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                            {t('admin.seasonalPricing.fields.dateRange')} <span style={{ color: '#ff4d4f' }}>*</span>
                        </Text>
                        <RangePicker
                            value={startDate && endDate ? [startDate, endDate] : null}
                            onChange={handleDateRangeChange}
                            style={{ width: '100%' }}
                            size="large"
                            disabledDate={disabledDate}
                            format="MMM D, YYYY"
                            placeholder={[t('admin.seasonalPricing.placeholders.startDate'), t('admin.seasonalPricing.placeholders.endDate')]}
                        />
                    </div>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                            {t('admin.seasonalPricing.fields.pricePerNight')} <span style={{ color: '#ff4d4f' }}>*</span>
                        </Text>
                        <Space.Compact>
                            <InputNumber
                                value={price}
                                onChange={setPrice}
                                style={{ width: '100%' }}
                                size="large"
                                min={0}
                                prefix={<EuroOutlined />}
                                placeholder={t('admin.seasonalPricing.placeholders.price')}
                            />
                            <Button size="large" disabled style={{ pointerEvents: 'none' }}>
                                /{t('admin.seasonalPricing.night')}
                            </Button>
                        </Space.Compact>
                    </div>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        size="large"
                        block
                        style={{ backgroundColor: '#4d6765', borderColor: '#4d6765' }}
                    >
                        {t('admin.seasonalPricing.addButton')}
                    </Button>
                </Flex>
            </div>

            {/* Seasonal Prices Table */}
            {seasonalPrices.length > 0 ? (
                <>
                    <Title level={5} style={{ marginBottom: '16px' }}>
                        {t('admin.seasonalPricing.currentTitle')} ({seasonalPrices.length})
                    </Title>
                    <Table
                        columns={columns}
                        dataSource={seasonalPrices}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                    />
                </>
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px',
                    border: '1px dashed #d9d9d9'
                }}>
                    <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                        {t('admin.seasonalPricing.empty.title')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('admin.seasonalPricing.empty.description')}
                    </Text>
                </div>
            )}
        </Card>
    );
};

export default SeasonalPricing;